import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { preauthStorage, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck, Copy, Download, KeyRound, LogOut, QrCode } from "lucide-react";
import { toast } from "sonner";

const LEN = 6;

export default function TwoFactorSetup() {
  const { user, enrollStart, enrollConfirm, logout } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState(null); // {qr_data_url, manual_secret, ...}
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(true);
  const [backupCodes, setBackupCodes] = useState(null); // shown once after success

  useEffect(() => {
    if (user) return;                                     // already signed in
    if (!preauthStorage.get()) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const data = await enrollStart();
        setSetup(data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setStarting(false);
      }
    })();
  }, []);

  // If already signed in, kick back to the app
  if (user) return <Navigate to="/" replace />;

  const submit = async () => {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== LEN) { setError("Enter the 6-digit code from your authenticator app."); return; }
    setBusy(true); setError("");
    try {
      const data = await enrollConfirm(digits, trust);
      setBackupCodes(data.backup_codes || []);
    } catch (e) {
      setError(formatApiError(e) || "That code didn't match.");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const copyBackup = async () => {
    try {
      await navigator.clipboard.writeText((backupCodes || []).join("\n"));
      toast.success("Backup codes copied");
    } catch { toast.error("Couldn't copy — please save manually"); }
  };
  const downloadBackup = () => {
    const blob = new Blob(
      [`D4PS Portal — 2FA Backup Codes\nAccount: ${setup?.account || ""}\n\n${(backupCodes || []).join("\n")}\n\nEach code can be used ONCE. Store somewhere safe.\n`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "d4ps-2fa-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };
  const finish = () => {
    setBackupCodes(null);
    navigate("/", { replace: true });
  };

  const onSignOut = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div data-testid="twofa-setup-page" className="min-h-screen bg-slate-950 text-white relative overflow-hidden flex items-center justify-center px-6 py-10">
      <div
        className="absolute inset-0 opacity-[0.22]"
        style={{ background: "radial-gradient(50% 60% at 50% 0%, rgba(249,115,22,0.5), transparent 60%), radial-gradient(40% 50% at 0% 100%, rgba(255,255,255,0.10), transparent 60%)" }}
      />
      <div className="relative w-full max-w-lg">
        <div className="flex items-center gap-3 mb-8">
          <img src="/d4ps-logo.png" alt="D4PS" className="h-10 w-10 rounded-sm object-contain bg-black/40 p-1" />
          <div>
            <div className="font-display text-lg font-semibold tracking-tight">D4PS</div>
            <div className="text-[9px] tracking-[0.32em] text-slate-400 mt-0.5">COURIERS &amp; LOGISTICS</div>
          </div>
        </div>

        <div className="text-[11px] uppercase tracking-[0.32em] text-orange-400/95 font-semibold inline-flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-orange-500" />
          Secure sign-in · Two-factor
        </div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight">
          {backupCodes ? "Save your backup codes" : "Set up authenticator"}
        </h1>
        <div className="mt-3 h-[3px] w-12 bg-orange-500" />

        {!backupCodes && (
          <>
            <p className="mt-5 text-slate-300 text-sm leading-relaxed">
              Two-step sign-in is required for every account. Scan the QR code below with{" "}
              <span className="text-white">Google Authenticator</span>, <span className="text-white">Authy</span> or{" "}
              <span className="text-white">1Password</span>, then enter the 6-digit code it shows.
            </p>

            {starting && (
              <div className="mt-8 rounded-sm bg-slate-900/60 border border-slate-800 p-6 flex items-center gap-3 text-slate-300">
                <Loader2 className="h-4 w-4 animate-spin text-orange-500" /> Preparing your QR code…
              </div>
            )}

            {!!setup && (
              <div className="mt-6 rounded-sm bg-slate-900/60 border border-slate-800 p-5 flex gap-5 items-start">
                <img
                  src={setup.qr_data_url}
                  alt="Scan with your authenticator app"
                  data-testid="twofa-qr"
                  className="h-44 w-44 bg-white rounded-sm p-2 border border-slate-800 shrink-0"
                />
                <div className="min-w-0 space-y-3 flex-1">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Account</div>
                    <div className="text-sm text-slate-200 font-medium truncate">{setup.account}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-1.5">
                      <QrCode className="h-3 w-3" /> Can&apos;t scan? Enter key manually
                    </div>
                    <div
                      data-testid="twofa-manual-secret"
                      className="mt-1 text-[13px] break-all font-mono text-orange-300 select-all"
                    >
                      {setup.manual_secret}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(setup.manual_secret).then(
                          () => toast.success("Setup key copied"),
                          () => toast.error("Couldn't copy")
                        );
                      }}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200"
                    >
                      <Copy className="h-3 w-3" /> Copy key
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <label className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Enter 6-digit code</label>
              <Input
                data-testid="twofa-enroll-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={LEN}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, LEN))}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                className="mt-2 h-12 rounded-sm bg-slate-900/70 border-slate-700 text-white text-center font-display text-xl tabular-nums tracking-[0.5em]"
              />
            </div>

            <label className="mt-4 flex items-center gap-2 text-sm text-slate-300">
              <Checkbox
                data-testid="twofa-trust-device"
                checked={trust}
                onCheckedChange={(v) => setTrust(!!v)}
                className="border-slate-500 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              Trust this device for 15 days
            </label>

            {error && (
              <div data-testid="twofa-error" className="mt-4 text-sm text-rose-300 bg-rose-950/40 border border-rose-900/60 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <Button
              data-testid="twofa-enroll-submit"
              onClick={submit}
              disabled={busy || starting}
              className="mt-6 w-full h-11 rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-medium"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Enable two-factor
            </Button>
          </>
        )}

        {backupCodes && (
          <>
            <p className="mt-5 text-slate-300 text-sm leading-relaxed">
              Store these one-time codes somewhere safe (password manager, printed copy). Each one lets you sign in <span className="text-white">once</span> if you lose access to your authenticator app.
            </p>
            <div
              data-testid="twofa-backup-codes"
              className="mt-5 rounded-sm bg-slate-900/60 border border-slate-800 p-5 grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-[15px] tabular-nums text-orange-200"
            >
              {backupCodes.map((c, i) => (
                <div key={c} className="flex items-center gap-2">
                  <span className="text-slate-500 text-[11px] w-4 text-right">{i + 1}.</span>
                  <span className="select-all">{c}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                data-testid="twofa-backup-copy"
                variant="outline"
                onClick={copyBackup}
                className="rounded-sm bg-slate-900 border-slate-700 text-slate-100 hover:bg-slate-800"
              >
                <Copy className="h-4 w-4 mr-2" /> Copy
              </Button>
              <Button
                data-testid="twofa-backup-download"
                variant="outline"
                onClick={downloadBackup}
                className="rounded-sm bg-slate-900 border-slate-700 text-slate-100 hover:bg-slate-800"
              >
                <Download className="h-4 w-4 mr-2" /> Download
              </Button>
              <Button
                data-testid="twofa-backup-continue"
                onClick={finish}
                className="ml-auto rounded-sm bg-orange-500 hover:bg-orange-600"
              >
                I&apos;ve saved them — continue
              </Button>
            </div>
          </>
        )}

        <div className="mt-10 pt-6 border-t border-slate-800/60 flex items-center justify-between text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><KeyRound className="h-3 w-3" /> {setup?.account || ""}</span>
          <button onClick={onSignOut} className="inline-flex items-center gap-1 hover:text-slate-300" data-testid="twofa-signout">
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
